import React, {useEffect, useState, useRef} from "react";
import TextInput from "../../../elements/TextInput";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye, faInfoCircle, faEyeSlash} from "@fortawesome/free-solid-svg-icons";
import UserRolesCard from "../users/UserRolesCard";
import {Button} from "react-bootstrap";
import Select from "../../../elements/Select";
import Toggler from "../../../elements/Toggler";
import EmployeeAddressCard from "./EmployeeAddressCard";
import EmployeeRecruitmentDataCard from "./EmployeeRecruitmentDataCard";
import EmployeeIdentityCard from "./EmployeeIdentityCard";
import QDatePicker from "../../../elements/QDatePicker";
import Select2 from "../../../elements/Select2";
import EmployeePhotoCard from "./EmployeePhotoCard";


const EmployeeSection = ({title, description, children, id, wide = false}) => (
  <section className={`employee-form-section ${wide ? "employee-section-wide" : ""}`.trim()} id={id}>
    <div className="employee-form-section-header">
      <strong>{title}</strong>
      {description && <span>{description}</span>}
    </div>
    <div className="employee-form-grid">{children}</div>
  </section>
);

const EmployeeField = ({label, required, children, span = 1, className = ""}) => (
  <div className={`employee-form-field employee-span-${span} ${className}`.trim()}>
    <label>{required && <span className="required">*</span>} {label}</label>
    <div className="employee-form-control">{children}</div>
  </div>
);

const EmployeeToggleField = ({label, children, span = 1, hint}) => (
  <div className={`employee-toggle-field employee-span-${span}`}>
    <div>
      <strong>{label}</strong>
      {hint && <small>{hint}</small>}
    </div>
    {children}
  </div>
);

const TabEmployeePersonalData = ({state, rolesList}) => {

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordsEqual, setPasswordsEqual] = useState(true);
  const [passwordIsValid, setPasswordIsValid] = useState(true);
  const [oldPwdVisible, setOldPwdVisible] = useState(false);
  const [newPwdVisible, setNewPwdVisible] = useState(false);
  const [confPwdVisible, setConfPwdVisible] = useState(false);
  const [startDate, setStartDate] = useState(state.currentItem.dateOfBirth);

  const oldPasswordRef = useRef();
  const passwordRef = useRef();
  const passwordConfirmationRef = useRef();
  const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%]).{8,24}$/; // requires 1 uppercase letter, 1 lowercase letter, 1 digit, 1 special char and has to be 8-24 chars long

  const currentContract = state.currentItem.latestContract;
  const isAdding = state.currentFormState.formAddingDataMode;
  const existingAppUser = !isAdding && !!state.backupItem.appUser;
  const appUser = !!state.backupItem.appUser || !!state.currentItem.appUser;
  const passwordSectionVisible = isAdding ? appUser : existingAppUser ? showPasswordSection : appUser;
  const oldPasswordRequired = !isAdding && existingAppUser && showPasswordSection;

  const clearPasswordFields = () => {
    const passwordField = document.getElementById("password");
    const passwordConfirmationField = document.getElementById("passwordConf");
    const oldPasswordField = document.getElementById("oldPassword");

    if (passwordField) passwordField.value = "";
    if (passwordConfirmationField) passwordConfirmationField.value = "";
    if (oldPasswordField) oldPasswordField.value = "";

    setPasswordsEqual(true);
    setPasswordIsValid(true);
    state.setValidPassword(true);
    state.setCurrentItem(currentItem => {
      if (currentItem.password === null || currentItem.password === undefined || currentItem.password === "") return currentItem;
      return {...currentItem, password: null};
    });
  }

  const handleAppUserChange = (e) => {
    if (existingAppUser) return;

    const checked = e.target.checked;
    setShowPasswordSection(false);
    state.setItemChanged(itemChanged => !itemChanged);
    state.setCurrentItem(currentItem => {
      const currentRoles = currentItem.userRoleDTOS || currentItem.userRoles || [];
      const employeeRole = (rolesList || []).find(role => String(role.name || "").toUpperCase() === "EMPLOYEE");
      const nextRoles = checked && currentRoles.length === 0 && employeeRole
        ? [{id: employeeRole.id, name: employeeRole.name, description: employeeRole.description, systemRole: employeeRole.systemRole}]
        : checked ? currentRoles : [];
      return {
        ...currentItem,
        appUser: checked,
        password: null,
        userRoleDTOS: nextRoles,
        userRoles: nextRoles
      };
    });
    state.setCurrentFormState({...state.currentFormState, formSaveButtonDisabled: false});

    if (!checked) {
      clearPasswordFields();
    }
  }

  function createMailbox() {
    if (!state.currentItem.companyEmail|| state.currentItem.companyEmail === "") {
      state.setCurrentItem(prev => ({
        ...prev,
        companyEmail: state.currentItem.login + "@farmailuzji.pl"
      }))
    }
  }

  const passwordToggler = (passwordField, passwordTogglerIcon, setPasswordVisible) => {
    const password = document.getElementById(passwordField);
    const toggler = document.getElementById(passwordTogglerIcon);
    const isPasswordVisible = password.type === "password";
    password.type = isPasswordVisible ? "text" : "password";
    toggler.classList.toggle("pwd_visible", isPasswordVisible);
    setPasswordVisible(isPasswordVisible);
  };


  useEffect(() => {
    if (!passwordSectionVisible) return;

    const passwordField = document.getElementById("password");
    if (!passwordField) return;

    const passwordValue = passwordField.value === "" ? null : passwordField.value;

    if (passwordsEqual && state.validPassword) {
      state.setCurrentItem(currentItem => {
        if (currentItem.password === passwordValue) return currentItem;
        return {...currentItem, password: passwordValue};
      });
      state.setCurrentFormState(currentFormState => ({...currentFormState, formSaveButtonDisabled: false}));
    } else {
      state.setCurrentItem(currentItem => {
        if (currentItem.password === null || currentItem.password === undefined) return currentItem;
        return {...currentItem, password: null};
      });
      state.setCurrentFormState(currentFormState => ({...currentFormState, formSaveButtonDisabled: true}));
    }
  }, [passwordsEqual, state.validPassword, passwordSectionVisible])


  useEffect(() => {
    if (passwordSectionVisible) return;
    clearPasswordFields();
  }, [passwordSectionVisible])


  const comparePasswords = () => {
    if (!passwordSectionVisible) return;

    const passwordField = document.getElementById("password");
    const passwordConfirmationField = document.getElementById("passwordConf");
    if (!passwordField || !passwordConfirmationField) return;

    const newPassword = passwordField.value;
    const passwordsAreEqual = passwordField.value === passwordConfirmationField.value;
    setPasswordsEqual(passwordsAreEqual);
    //console.log("REGEX test: " + PWD_REGEX.test(newPassword));

    if ((newPassword && PWD_REGEX.test(newPassword)) || newPassword === "123") {
      //console.log("Passwords equal and regex passed");
      setPasswordIsValid(true);
      state.setValidPassword(true);
    }

    if (newPassword && !PWD_REGEX.test(newPassword) && newPassword !== "123") {
      //console.log("Regex not passed or password <> 123");
      setPasswordIsValid(false);
      state.setValidPassword(false);
    }
  }

  useEffect(() => {
    let x;
    if (state.currentItem.latestContract) {
      x = state.currentItem.latestContract;
      console.log(x);

    } else {
      // console.log("NO CONTRACT");
    }
  },);


  const togglePasswordSection = () => {
    if (!existingAppUser) return;

    const passwordSection = document.getElementById("passwordSection");
    const button = document.getElementById("change-password");

    if (passwordSection && passwordSection.classList.contains("offscreen")) {
      if (button) button.innerText = "Anuluj";
      passwordSection.classList.remove("offscreen");
      setShowPasswordSection(true);

    } else {
      const backupItem = state.backupItem;
      clearPasswordFields();

      const propertiesAreEqual = Object.keys(state.currentItem).every(key => {
        if (key === 'password') return true;
        return state.currentItem[key] === backupItem[key];
      });

      if (state.currentItem.password === null && propertiesAreEqual) {
        state.setCurrentFormState({
          ...state.currentFormState,
          formSaveButtonDisabled: true,
        });
      }

      if (button) button.innerText = "Zmień hasło";
      if (passwordSection) passwordSection.classList.add("offscreen");
      setShowPasswordSection(false);
    }
  }

  const identityCardType = {
    label: "Typ dokumentu tożsamości",
    itemName: "identityCardType",
    propertyName: "identityCardTypeId"
  }

  const identityCard = {
    cardId: "identityCardId",
    cardIssueDate: "identityCardIssueDate",
    cardExpiryDate: "identityCardExpiryDate",
    issuedBy: "issuedBy",
    issueLocation: "issueLocation"
  }

  const primaryCountry = {
    label: "Kraj",
    itemName: "primaryAddressCountry",
    propertyName: "primaryAddressCountryId"
  }

  const secondaryCountry = {
    label: "Kraj",
    itemName: "secondaryAddressCountry",
    propertyName: "secondaryAddressCountryId"
  }

  const primaryAddress = {
    street: "primaryAddressStreet",
    streetNumber: "primaryAddressStreetNumber",
    postalCode: "primaryAddressPostalCode",
    city: "primaryAddressCity",
  }

  const secondaryAddress = {
    street: "secondaryAddressStreet",
    streetNumber: "secondaryAddressStreetNumber",
    postalCode: "secondaryAddressPostalCode",
    city: "secondaryAddressCity",
  }


  const contractStatusLabel = currentContract
    ? currentContract.isValid === "Y"
      ? `Aktywna do ${currentContract.dateTo || "bezterminowo"}`
      : currentContract.isValid === "E"
        ? `Wygasa ${currentContract.dateTo || ""}`
        : `Wygasła ${currentContract.dateTo || ""}`
    : "Brak aktualnej umowy";

  const formatContractAmount = value => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return null;
    return new Intl.NumberFormat("pl-PL", {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(amount);
  };

  const contractBillingLabel = (() => {
    if (!currentContract) return null;
    if (currentContract.billingType === "HOURLY") {
      const amount = formatContractAmount(currentContract.hourlyRate);
      return amount ? `Godzinowo · ${amount} zł brutto / godz.` : "Godzinowo · brak stawki godzinowej";
    }
    if (currentContract.billingType === "MONTHLY") {
      const amount = formatContractAmount(currentContract.monthlyRate);
      return amount ? `Miesięcznie · ${amount} zł brutto / mies.` : "Miesięcznie · brak kwoty miesięcznej";
    }
    return "Forma rozliczenia nieokreślona";
  })();

  return (
    <div className="employee-personal-layout employee-section-columns">
      <div className="employee-layout-column">
        <EmployeeSection
          id="basic-data"
          title="Dane podstawowe"
          description="Podstawowe dane identyfikacyjne i kontakt awaryjny."
        >
          <EmployeeField label="Imię" required>
            <TextInput propertyName="firstName" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Drugie imię">
            <TextInput propertyName="secondName" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Nazwisko" required>
            <TextInput propertyName="lastName" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Płeć" required>
            <Select
              label="Płeć"
              propertyName="gender"
              required={false}
              state={state}
              itemsList={state.gendersList}
              itemName="gender"
              displayNames={["name"]}
            />
          </EmployeeField>
          <EmployeeField label="Data urodzenia" required>
            <QDatePicker
              pickerId="dateOfBirth"
              errorParagraphId="wrongBirthDateFormat"
              state={state}
              propertyName="dateOfBirth"
              startDate={startDate}
              setStartDate={setStartDate}
            />
            <small id="wrongBirthDateFormat" className="err_info offscreen employee-field-note">
              <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;Nieprawidłowy format daty
            </small>
          </EmployeeField>
          <EmployeeField label="Miejsce urodzenia">
            <TextInput propertyName="placeOfBirth" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Obywatelstwo">
            <select className="form-select" value={state.currentItem.citizenship || "Polska"} onChange={e => { state.setCurrentItem(v => ({...v, citizenship: e.target.value})); state.setCurrentFormState(v => ({...v, formSaveButtonDisabled:false})); }}>
              <option value="Polska">Polska</option><option value="Ukraina">Ukraina</option><option value="inne">inne</option>
            </select>
          </EmployeeField>
          <EmployeeField label="Stan cywilny">
            <Select
              label="Stan cywilny"
              propertyName="maritalStatus"
              required={false}
              state={state}
              itemsList={state.maritalStatusesList}
              itemName="maritalStatus"
              displayNames={["name"]}
            />
          </EmployeeField>
          <EmployeeField label="Kontakt awaryjny">
            <TextInput propertyName="iceContact" required={true} state={state}/>
          </EmployeeField>
        </EmployeeSection>
        <EmployeeSection
          id="contact-communication"
          title="Kontakt i komunikacja"
          description="Adresy e-mail, telefony i kanały powiadomień."
        >
          <EmployeeField label="Firmowy e-mail" required span={2}>
            <div className="employee-inline-control">
              <TextInput propertyName="companyEmail" required={true} state={state}/>
              <Button variant="outline-secondary" size="sm" disabled={!!state.currentItem.companyEmail} onClick={createMailbox}>Utwórz</Button>
            </div>
          </EmployeeField>
          <EmployeeField label="Prywatny e-mail" span={2}>
            <TextInput propertyName="privateEmail" required={true} state={state}/>
          </EmployeeField>
          <EmployeeToggleField label="Komunikacja e-mail" span={2}>
            <Toggler propertyName="communicationByEmail" state={state}/>
          </EmployeeToggleField>
          <EmployeeToggleField label="Komunikacja SMS" span={2}>
            <Toggler propertyName="communicationBySMS" state={state}/>
          </EmployeeToggleField>
          <EmployeeField label="Główny telefon" span={2}>
            <TextInput propertyName="primaryPhone" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Pomocniczy telefon" span={2}>
            <TextInput propertyName="secondaryPhone" required={true} state={state}/>
          </EmployeeField>
        </EmployeeSection>
        <EmployeeSection
          id="addresses"
          title="Adresy"
          wide
          description="Adres zameldowania oraz adres zamieszkania/korespondencyjny."
        >
          <div className="employee-span-2 employee-nested-card">
            <EmployeeAddressCard
              cardHeader="Adres zameldowania"
              state={state}
              addressProps={primaryAddress}
              countriesList={state.countriesList}
              countryProps={primaryCountry}
            />
          </div>
          <div className="employee-span-2 employee-nested-card">
            <EmployeeAddressCard
              cardHeader="Adres zamieszkania/korespondencyjny"
              state={state}
              addressProps={secondaryAddress}
              countriesList={state.countriesList}
              countryProps={secondaryCountry}
            />
          </div>
        </EmployeeSection>
        <EmployeeSection
          id="personal-photo"
          title="Zdjęcie pracownika"
          description="Zdjęcie w kartotece. Kierownik, Kadry / HR lub administrator mogą dodać je również z telefonu."
        >
          <EmployeePhotoCard employeeId={state.currentItem?.id}/>
        </EmployeeSection>
      </div>

      <div className="employee-layout-column">
        <EmployeeSection
          id="account-access"
          title="Konto i dostęp"
          description="Dane logowania, aktywność konta i karta RCP."
        >
          <EmployeeToggleField label="Aktywny">
            <Toggler propertyName="enabled" state={state}/>
          </EmployeeToggleField>

          <EmployeeField label="Login" required span={1}>
            <TextInput
              propertyName="login"
              required={true}
              state={state}
              disabled={!state.currentFormState.formAddingDataMode}
            />
          </EmployeeField>

          <EmployeeField label="Karta RCP" span={1}>
            <Select
              label="Karta RCP"
              propertyName="timeCard"
              required={false}
              state={state}
              itemsList={state.timeCardsList.filter(tc =>
                tc.valid && (tc.employeeId === null || tc.employeeId === state.currentItem.id)
              )}
              itemName="timeCard"
              displayNames={["cardId"]}
            />
            {state.currentItem.timeCard && !state.currentItem.timeCard.valid && (
              <small className="err_info employee-field-note">Karta jest nieważna</small>
            )}
          </EmployeeField>

          <EmployeeToggleField
            label={existingAppUser ? "Użytkownik aplikacji" : "Utwórz użytkownika aplikacji"}
            span={1}
          >
            <input
              className="form-check-input employee-account-checkbox"
              type="checkbox"
              id="appUser"
              checked={appUser}
              disabled={existingAppUser}
              onChange={handleAppUserChange}
            />
          </EmployeeToggleField>

          {existingAppUser && (
            <div className="employee-account-status employee-span-4">
              <span className={`employee-account-badge ${state.currentItem.appUserEnabled ? "active" : "disabled"}`}>
                {state.currentItem.appUserEnabled ? "Konto aktywne" : "Konto wyłączone"}
              </span>
              <span className="text-muted">Konto istnieje. Wyłączenie konta wykonuje administrator w module „Użytkownicy aplikacji”.</span>
              <Button variant="outline-secondary" size="sm" id="change-password" onClick={togglePasswordSection}>
                Zmień hasło
              </Button>
            </div>
          )}

          {!isAdding && !existingAppUser && appUser && (
            <div className="employee-inline-info employee-span-4">
              Po zapisaniu pracownik zostanie dodany jako użytkownik aplikacji.
            </div>
          )}

          {appUser && (
            <div className="employee-span-4" id="roles">
              <UserRolesCard state={state} rolesList={rolesList || []}/>
            </div>
          )}

          <div id="passwordSection" className={`employee-password-panel employee-span-4 ${passwordSectionVisible ? "" : "offscreen"}`}>
            <div className="employee-password-grid">
              {oldPasswordRequired && (
                <EmployeeField label="Stare hasło" required>
                  <div className="employee-password-control">
                    <input className="form-control" type="password" id="oldPassword" required ref={oldPasswordRef}/>
                    <span id="oldPassword_toggler" className="password_toggler"
                          onClick={() => passwordToggler("oldPassword", "oldPassword_toggler", setOldPwdVisible)}>
                      <FontAwesomeIcon icon={oldPwdVisible ? faEye : faEyeSlash}/>
                    </span>
                  </div>
                </EmployeeField>
              )}

              <EmployeeField label={oldPasswordRequired ? "Nowe hasło" : "Hasło"} required>
                <div className="employee-password-control">
                  <input
                    className="form-control"
                    type="password"
                    id="password"
                    onChange={comparePasswords}
                    required={passwordSectionVisible}
                    ref={passwordRef}
                  />
                  <span id="password_toggler" className="password_toggler"
                        onClick={() => passwordToggler("password", "password_toggler", setNewPwdVisible)}>
                    <FontAwesomeIcon icon={newPwdVisible ? faEye : faEyeSlash}/>
                  </span>
                </div>
                <small className={!passwordIsValid ? "err_info employee-field-note" : "offscreen"}>
                  <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;8–24 znaki, wielka i mała litera, cyfra i znak specjalny ! @ # $ %.
                </small>
              </EmployeeField>

              <EmployeeField label="Potwierdź hasło" required>
                <div className="employee-password-control">
                  <input
                    className="form-control"
                    type="password"
                    id="passwordConf"
                    onChange={comparePasswords}
                    required={passwordSectionVisible}
                    ref={passwordConfirmationRef}
                  />
                  <span id="passwordConf_toggler" className="password_toggler"
                        onClick={() => passwordToggler("passwordConf", "passwordConf_toggler", setConfPwdVisible)}>
                    <FontAwesomeIcon icon={confPwdVisible ? faEye : faEyeSlash}/>
                  </span>
                </div>
                <small className={!passwordsEqual ? "err_info employee-field-note" : "offscreen"}>
                  <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;Podane hasła są różne.
                </small>
              </EmployeeField>
            </div>
          </div>
        </EmployeeSection>
        <EmployeeSection
          id="employee-status-settlements"
          title="Status pracownika i rozliczenia"
          wide
          description="Status ucznia/studenta, umowa, bank, ZUS/KRUS i ograniczenia."
        >
          <EmployeeToggleField label="Uczeń">
            <Toggler propertyName="pupil" state={state}/>
          </EmployeeToggleField>
          <EmployeeToggleField label="Student">
            <Toggler propertyName="student" state={state}/>
          </EmployeeToggleField>
          <EmployeeToggleField label="KRUS"><Toggler propertyName="krus" state={state}/></EmployeeToggleField>
          <EmployeeToggleField label="Emeryt"><Toggler propertyName="retired" state={state}/></EmployeeToggleField>
          <EmployeeToggleField label="Rencista"><Toggler propertyName="pensioner" state={state}/></EmployeeToggleField>
          <EmployeeToggleField label="Członkowie rodziny do ubezpieczenia"><Toggler propertyName="familyHealthInsurance" state={state}/></EmployeeToggleField>
          <EmployeeField label="Stopień niepełnosprawności" span={2}>
            <select className="form-select" value={state.currentItem.disabilityDegree || "brak"} onChange={e => { state.setCurrentItem(v => ({...v, disabilityDegree:e.target.value})); state.setCurrentFormState(v => ({...v, formSaveButtonDisabled:false})); }}><option value="brak">Brak</option><option value="lekki">Lekki</option><option value="umiarkowany">Umiarkowany</option><option value="znaczny">Znaczny</option></select>
          </EmployeeField>
          <EmployeeToggleField label="Zgoda rodziców">
            <Toggler propertyName="parentalConsent" state={state}/>
          </EmployeeToggleField>
          <EmployeeToggleField label="Tylko weekendy i dni wolne" span={2}>
            <Toggler propertyName="availableOnlyWeekends" state={state}/>
          </EmployeeToggleField>
          <EmployeeField label="Numer legitymacji" span={2}>
            <TextInput propertyName="studentId" required={false} state={state} disabled={!state.currentItem.student && !state.currentItem.pupil}/>
          </EmployeeField>

          <div className="employee-contract-summary employee-span-4">
            <div>
              <strong>Aktualna umowa</strong>
              <span>{currentContract ? `${currentContract.contractType || ""} ${currentContract.identifier || ""}`.trim() : "Brak aktualnej umowy"}</span>
              {contractBillingLabel && <span className="employee-contract-billing">{contractBillingLabel}</span>}
            </div>
            <span className={`employee-contract-badge ${currentContract?.isValid === "Y" ? "active" : currentContract?.isValid === "E" ? "warning" : "expired"}`}>
              {contractStatusLabel}
            </span>
          </div>

          {Array.isArray(state.currentItem.contracts) && state.currentItem.contracts.length > 0 && (
            <div className="employee-span-4 border rounded p-2 bg-light">
              <div className="small fw-semibold mb-1">Wszystkie umowy pracownika</div>
              <div className="d-flex flex-wrap gap-2">
                {state.currentItem.contracts.map(contract => (
                  <span key={contract.id} className={`badge ${contract.defaultForScheduling ? "bg-primary" : "bg-secondary"}`}>
                    {contract.contractType || "Umowa"} {contract.identifier || ""} · {contract.dateFrom || "?"} – {contract.dateTo || "bezterminowo"}
                    {contract.hiringCompanyName ? ` · ${contract.hiringCompanyName}` : ""}
                    {contract.defaultForScheduling ? " · domyślna do grafiku" : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          <EmployeeField label="Konto bankowe" span={2}><TextInput propertyName="bankAccountNumber" required={false} state={state}/></EmployeeField>
          <EmployeeField label="Nazwa banku" span={2}><TextInput propertyName="bankName" required={false} state={state}/></EmployeeField>
          <EmployeeRecruitmentDataCard employeeId={state.currentItem.id}/>
          <EmployeeToggleField
            label="Rachunek należy do pracownika"
            span={2}
            hint="Wyłącz, jeśli wynagrodzenie ma być przelewane na konto rodzica lub innej osoby."
          >
            <Toggler propertyName="bankAccountOwnedByEmployee" state={state}/>
          </EmployeeToggleField>
          {state.currentItem.bankAccountOwnedByEmployee === false && (
            <>
              <EmployeeField label="Właściciel rachunku" required span={2}>
                <TextInput propertyName="bankAccountHolderName" required={true} state={state}/>
              </EmployeeField>
              <EmployeeToggleField
                label="Zgoda na wypłatę na rachunek osoby trzeciej"
                span={2}
                hint="Pracownik lub zleceniobiorca potwierdza zgodę na przekazywanie wynagrodzenia na wskazany rachunek."
              >
                <Toggler propertyName="bankAccountThirdPartyConsent" state={state}/>
              </EmployeeToggleField>
              <EmployeeField label="Data oświadczenia" required span={2}>
                <TextInput propertyName="bankAccountConsentDate" type="date" required={true} state={state}/>
              </EmployeeField>
              <div className="employee-span-4 small text-muted">
                Po zapisaniu danych dołącz podpisane oświadczenie w zakładce Dokumenty jako „Zgoda na wypłatę na rachunek osoby trzeciej”.
              </div>
            </>
          )}
          <EmployeeField label="Oddział NFZ">
            <Select2
              label="Oddział NFZ"
              propertyName="nfzBranch"
              required={false}
              state={state}
              itemsList={state.nfzBranchesList}
              itemName="nfzBranch"
              displayNames={["name"]}
            />
          </EmployeeField>
          <EmployeeField label="Urząd Skarbowy">
            <Select2
              label="Urząd Skarbowy"
              propertyName="taxOffice"
              required={false}
              state={state}
              itemsList={state.taxOfficesList}
              itemName="taxOffice"
              displayNames={["name"]}
            />
          </EmployeeField>
          <EmployeeToggleField label="Niechciany" span={1}>
            <Toggler propertyName="unwanted" state={state}/>
          </EmployeeToggleField>
          <EmployeeField label="Powód odrzucenia" span={3}>
            <TextInput propertyName="unwantedReason" required={true} state={state} disabled={!state.currentItem.unwanted}/>
          </EmployeeField>
        </EmployeeSection>
        <EmployeeSection
          id="identity-data"
          title="Dane identyfikacyjne"
          wide
          description="Dane urzędowe i dokument tożsamości."
        >
          <EmployeeField label="Imię matki">
            <TextInput propertyName="mothersName" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Imię ojca">
            <TextInput propertyName="fathersName" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="Nazwisko panieńskie">
            <TextInput propertyName="maidenName" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="PESEL">
            <TextInput propertyName="pesel" required={true} state={state}/>
          </EmployeeField>
          <EmployeeField label="NIP">
            <TextInput propertyName="nip" required={true} state={state}/>
          </EmployeeField>
          <div className="employee-span-4 employee-nested-card">
            <EmployeeIdentityCard
              cardHeader="Dokument tożsamości"
              state={state}
              identityCardProps={identityCard}
              identityCardTypeList={state.identityCardTypesList}
              identityCardTypeProps={identityCardType}
            />
          </div>
        </EmployeeSection>
      </div>
    </div>
  );
};

export default TabEmployeePersonalData;


